/**
 * Presença da loja do vendedor — fonte de verdade no Firestore.
 */

import type { Vendor } from '../types';
import { isStoreOpen, type StorePresenceEntity } from './storePresence';

export {
  parseLastSeenMs,
  VENDOR_PRESENCE_TTL_MS,
  VENDOR_PRESENCE_HEARTBEAT_MS,
  isStoreOpen,
} from './storePresence';

type PresenceCarrier = Pick<Vendor, 'id' | 'storeOnline' | 'lastSeenAt'> | null | undefined;

/** Loja aceita pedidos agora. NÃO usar para filtrar vitrine. */
export function isVendorStoreLive(vendor: PresenceCarrier): boolean {
  return isStoreOpen(vendor as StorePresenceEntity);
}

/** Pin genérico histórico (App/CityHall) — não conta como localização real. */
const DEFAULT_FALLBACK_PIN = { lat: -3.7319, lng: -38.5267 };

function isDefaultFallbackPin(lat: number, lng: number): boolean {
  return (
    Math.abs(lat - DEFAULT_FALLBACK_PIN.lat) < 1e-4 &&
    Math.abs(lng - DEFAULT_FALLBACK_PIN.lng) < 1e-4
  );
}

/**
 * Endereço listável na vitrine / loja.
 * - CONFIRMED + lat/lng válidos (fluxo Etapa 4b)
 * - Legado: sem locationStatus (docs pré-reestruturação) + lat/lng reais (não pin default)
 * - PENDING_ADDRESS / NEEDS_REVIEW continuam fora até geocode/backfill
 */
export function hasConfirmedLocation(
  vendor: Pick<Vendor, 'location' | 'locationStatus'>
): boolean {
  const status = vendor.locationStatus;
  if (status === 'PENDING_ADDRESS' || status === 'NEEDS_REVIEW') return false;

  const loc = vendor.location;
  if (!loc || typeof loc !== 'object') return false;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return false;
  }
  if (isDefaultFallbackPin(lat, lng)) return false;

  // Explícito CONFIRMED, ou legado sem o campo (compatibilidade reversa pós-regressão).
  return status === 'CONFIRMED' || status == null || status === undefined;
}

/** Critério de listagem pública — regulado + endereço confirmado (Etapa 4b + legado). */
export function isPublicListingVendor(
  vendor: Pick<Vendor, 'id' | 'vendorStatus' | 'location' | 'locationStatus' | 'isTestAccount'>,
  supplierIds: ReadonlySet<string>
): boolean {
  return (
    vendor.isTestAccount !== true &&
    !supplierIds.has(vendor.id) &&
    vendor.vendorStatus !== 'SUSPENDED' &&
    vendor.vendorStatus !== 'PENDING_VERIFICATION' &&
    hasConfirmedLocation(vendor)
  );
}

/** Abertas primeiro; dentro do grupo, ordem alfabética por nome da loja. */
export function sortVendorsOpenFirst<T extends Pick<Vendor, 'storeOnline' | 'lastSeenAt' | 'storeName'>>(
  list: T[]
): T[] {
  return [...list].sort((a, b) => {
    const aOpen = isVendorStoreLive(a as PresenceCarrier) ? 1 : 0;
    const bOpen = isVendorStoreLive(b as PresenceCarrier) ? 1 : 0;
    if (bOpen !== aOpen) return bOpen - aOpen;
    return (a.storeName || '').localeCompare(b.storeName || '', 'pt-BR');
  });
}

export const VENDOR_STORE_CLOSED_MESSAGE =
  'Esta loja está fechada no momento. O vendedor precisa estar online no painel para receber pedidos.';

export const VENDOR_STORE_CLOSED_ORDER_LABEL = 'Loja fechada — volte mais tarde';

/** Alerta de endereço para painel Owner (fila / parceiros). */
export function getVendorLocationAlert(
  vendor: Pick<Vendor, 'location' | 'locationStatus' | 'geocodePending'>
): { tone: 'orange' | 'red'; label: string } | null {
  if (hasConfirmedLocation(vendor)) return null;

  const status = vendor.locationStatus;
  if (status === 'NEEDS_REVIEW' || vendor.geocodePending === true) {
    return {
      tone: 'red',
      label: 'Endereço precisa revisão — loja fora da vitrine',
    };
  }
  return {
    tone: 'orange',
    label: 'Endereço pendente de confirmação — loja não aparece na vitrine',
  };
}
